<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-auto bg-black/50 flex items-center justify-center"
  >
    <div class="bg-base-100 rounded-lg shadow-xl max-w-md w-full m-4">
      <!-- Modal header -->
      <div class="flex justify-between items-center p-4 border-b">
        <h3 class="text-lg font-medium">Kelionės informacija</h3>
        <button @click="closeModal" class="text-base-content/60 hover:text-base-content/80">
          <span class="sr-only">Uždaryti</span>
          <svg
            class="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Modal content -->
      <div v-if="isLoading" class="py-8 text-center">
        <span class="loading loading-spinner loading-lg text-base-content/60"></span>
      </div>
      <div v-else>
        <div class="p-4">
          <div>
            <div class="mb-6">
              <h1 class="font-semibold mb-2">Pavėžėjimas:</h1>
              <div
                v-if="tripInfo.assistant_needed"
                class="badge badge-outline badge-info mb-3"
              >
                Reikalinga palyda
              </div>
              <div
                v-if="tripInfo.child_seat_needed"
                class="badge badge-outline badge-info mb-3"
              >
                Reikalinga vaikiška kėdutė
              </div>
              <div
                v-if="tripInfo.wheelchair_needed"
                class="badge badge-outline badge-info mb-3"
              >
                Reikalingas neįgaliojo vež.
              </div>
              <div class="mb-2">
                <div v-if="tripInfo.notice" class="text-sm text-base-content/60">
                  Pavėžėjimo pastaba
                </div>
                <div>
                  {{ tripInfo.notice }}
                </div>
              </div>
              <div class="mb-2">
                <div class="text-sm text-base-content/60">Pradžia</div>
                <div>
                  {{ tripInfo.waypoints[0].time }} -
                  {{ tripInfo.waypoints[0].address }}
                </div>
              </div>
              <div>
                <div class="text-sm text-base-content/60">Pabaiga</div>
                <div>
                  {{ tripInfo.waypoints[1].time }} -
                  {{ tripInfo.waypoints[1].address }}
                </div>
              </div>
            </div>

            <div v-if="tripInfo.passengers && tripInfo.passengers.length > 0">
              <!-- List of passengers -->
              <h1 class="font-semibold mb-2">Keleiviai:</h1>
              <div class="flex items-center gap-2 mb-2">
                <div class="flex items-center gap-1">
                  <span>{{ tripInfo.person_regular_count }}</span>
                  <DefaultIcon name="passenger" size="w-4 h-4" />
                </div>
                <div class="flex items-center gap-1">
                  <span>{{ tripInfo.person_disabled_count }}</span>
                  <DefaultIcon name="wheelchair" size="w-4 h-4" />
                </div>
              </div>
              <div
                v-for="passenger in tripInfo.passengers"
                :key="passenger.id"
                class="mb-4"
              >
                <div class="space-y-2">
                  <!-- Name -->
                  <div>
                    <div class="text-sm text-base-content/60">Vardas, Pavardė</div>
                    <div>
                      {{ passenger.firstName }} {{ passenger.lastName }}
                    </div>
                  </div>

                  <!-- Phone -->
                  <div>
                    <div class="text-sm text-base-content/60">Telefonas</div>
                    <div class="flex items-center">
                      <div>{{ passenger.phone }}</div>
                      <a
                        :href="`tel:${passenger.phone}`"
                        class="ml-2 btn btn-sm btn-circle"
                      >
                        <DefaultIcon name="phone" size="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="py-4 text-center text-base-content/60">
              Keleivių informacija nerasta
            </div>
          </div>
        </div>

        <div class="px-4 pb-4">
          <Alert
            v-if="tripInfo.is_only_trip_in_travel"
            text="Tai vienintelis pavėžėjimas šioje kelionėje. Atšaukus pavėžėjimą, kelionė bus taip pat atšaukta."
            alert-class="alert-warning"
            icon="warning"
            :dismissible="false"
          />
        </div>

        <!-- Modal footer -->
        <div class="border-t p-4 flex">
          <button
            v-if="tripInfo.status == 'tripStatus1'"
            @click="confirmCancelTrip(tripInfo.id)"
            class="btn btn-error"
          >
            Atšaukti
          </button>
          <button @click="closeModal" class="btn ml-auto">Uždaryti</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import { useTripStore } from "@/stores/trips";
import { useRouter } from "vue-router";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Alert from "@/components/ui/alerts/DefaultAlert.vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  tripId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["close"]);

const tripStore = useTripStore();
const isLoading = ref(false);
const tripInfo = ref(null);

const router = useRouter();

const fetchTripInfo = async () => {
  if (!props.tripId) return;

  isLoading.value = true;

  try {
    const tripData = await tripStore.fetchTripById(props.tripId);
    tripInfo.value =
      tripData.passengers && tripData.passengers.length > 0 ? tripData : null;
  } catch (error) {
    console.error("Failed to fetch passenger info:", error);
    tripInfo.value = null;
  } finally {
    isLoading.value = false;
  }
};

const closeModal = () => {
  emit("close");
};

// Fetch passenger info when modal opens
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue && props.tripId) {
      fetchTripInfo();
    } else {
      tripInfo.value = null;
    }
  },
  { immediate: true },
);

// Cancel trip by setting travel id to null
const confirmCancelTrip = async (tripId) => {
  if (tripInfo.value.is_only_trip_in_travel) {
    if (
      confirm(
        "Ar Jūs tikrai norite atšaukti vientinelį pavėžėjimą kelionėje? Atšaukus pavėžėjimą bus atšaukta ir kelionė, Jūs būsite nukreipti į pagrindinį kelionių sąrašą.",
      )
    ) {
      await handleCancelTrip(tripId);
    }
  } else {
    if (confirm("Ar Jūs tikrai norite atšaukti pavėžėjimą?")) {
      await handleCancelTrip(tripId);
    }
  }
};

const handleCancelTrip = async (tripId) => {
  if (!tripId) return;

  isLoading.value = true;

  try {
    await tripStore.cancelTrip(tripId);

    // Redirect to travels if this was the only trip in the travel
    if (tripInfo.value.is_only_trip_in_travel) {
      router.push("/driving/travels");
    } else {
      // Emit event to notify parent that trip was updated
      emit("updated");
      // Close the modal
      closeModal();
    }
  } catch (error) {
    console.error("Failed to update trip:", error);
    // You might want to show an error message to the user
  } finally {
    isLoading.value = false;
  }
};
</script>
